import { useEffect, useMemo, useState } from "react";
import { FieldErrors, useForm } from "react-hook-form";
import _ from "lodash";
import { AssignedContractModel, ContractGroupModel, CustomFieldModel } from "src/api/contractGroup/contractgroup-type";
import { BaseButton, BaseModal, ContentsTitle } from "src/components";
import useNavigate from "src/hooks/usePartnerNavigate";
import { useQueryParams } from "src/hooks/useQueryParams";
import { useTabNavigation } from "src/hooks/useTabNavigation";
import { PagePath } from "src/pages/product/details";
import { useModal } from "src/recoil/modalState/hook";
import { CONTRACT_GROUP_TABS } from "../contractgroup-constant";
import { useContractGroup } from "../hooks/useContractGroup";
import ContractGroupCustomFieldForm from "./components/ContractGroupCustomFieldForm";
import ContractGroupInfoForm from "./components/ContractGroupInfoForm";
import EditCancelButton from "src/components/EditCancelButton";
import { usePartnerAuthority } from "src/hooks/usePartnerAuthority";
import GoToListButton from "src/components/GoToListButton";

// 커스텀 필드 목록 초기화 및 초기 데이터 설정
const useContractGroupForm = () => {
  const [initialData, setInitialData] = useState<ContractGroupModel | null>(null);
  const [customFieldList, setCustomFieldList] = useState<CustomFieldModel[]>([]);

  const { getCtGroupCustomField } = useContractGroup();

  useEffect(() => {
    const initializeForm = async () => {
      const result = await getCtGroupCustomField();
      const visibleCustomFields = result.filter((item) => item.isVisible);
      setCustomFieldList(visibleCustomFields);
      const defaultData: ContractGroupModel = {
        groupId: 0,
        groupName: "",
        inputList: visibleCustomFields.map((item) => ({
          fieldId: item.fieldId,
          inputValue: "",
          optionList: [],
        })),
      };

      setInitialData(defaultData);
    };

    initializeForm();
  }, []);

  return { initialData, customFieldList, setCustomFieldList };
};

// ================================ 폼 컴포넌트 ================================
const ContractGroupForm = () => {
  const navigate = useNavigate();
  const { queryParams } = useQueryParams();
  const { setBaseModal } = useModal();
  const [contractGroupDetail, setContractGroupDetail] = useState<ContractGroupModel>();
  const [contractSummaryList, setContractSummaryList] = useState<AssignedContractModel[]>([]); // 계약 매핑 목록

  const { isAuthority } = usePartnerAuthority();

  const [modalOpen, setModalOpen] = useState(false);

  const { activeTab, clickTab } = useTabNavigation({
    tabs: CONTRACT_GROUP_TABS,
  });

  /**
   * 변경내역 탭은 폼에서 직접 렌더링하지 않는다
   * - 등록: 저장된 계약그룹이 없으므로 안내 모달 노출
   * - 수정: 상세의 변경내역 탭으로 이동
   */
  const handleTabClick = (tab: { value: string; label: string }) => {
    if (tab.value !== "history") {
      clickTab(tab);
      return;
    }

    if (queryParams.id) {
      navigate(PagePath.contractGroup.detail.replace(":id", String(queryParams.id)) + "?tab=history");
      return;
    }

    setModalOpen(true);
  };
  const { addContractGroup, getContractGroupDetail, getContractMapOfGroupList } = useContractGroup();
  const { initialData, customFieldList } = useContractGroupForm();
  const { control, handleSubmit, setValue, reset, watch } = useForm<ContractGroupModel>({
    defaultValues: {
      groupId: 0,
      groupName: "",
      inputList: [],
    },
  });

  //계약 주체 정보
  const contractSubjectInfo = useMemo(() => {
    if (!contractSummaryList || contractSummaryList.length === 0) return null;

    // mbOrganizationId로 중복 제거하여 유니크한 조직들만 추출
    const uniqueOrgs = contractSummaryList.reduce((acc, contract) => {
      const orgId = contract.mbOrganizationId;
      const orgName = contract.orgName || "미지정";

      // 이미 해당 조직이 있는지 확인
      if (!acc.some((org) => org.mbOrganizationId === orgId)) {
        acc.push({
          mbOrganizationId: orgId,
          orgName,
          contractStartDate: contract.contractStartDate,
        });
      }

      return acc;
    }, [] as Array<{ mbOrganizationId: number; orgName: string; contractStartDate: string }>);

    // 가장 최근 contractStartDate를 가진 조직을 주 계약주체로 선정
    let primaryOrgName = "";
    let latestDate = new Date(0);

    uniqueOrgs.forEach((org) => {
      const contractDate = new Date(org.contractStartDate || 0);
      if (contractDate > latestDate) {
        latestDate = contractDate;
        primaryOrgName = org.orgName;
      }
    });

    const totalUniqueCount = uniqueOrgs.length;
    const otherCount = totalUniqueCount - 1; // 주 계약주체 제외한 나머지

    return {
      primaryOrgName,
      primaryCount: 1, // 주 계약주체는 항상 1개
      otherCount,
      totalCount: totalUniqueCount,
    };
  }, [contractSummaryList]);

  // 계약그룹 계약매핑 목록 조회
  const fetchContractMapOfGroup = async () => {
    const contractMapOfGroupData = await getContractMapOfGroupList(queryParams.id || "");
    setContractSummaryList(contractMapOfGroupData.contractListWithOrg);
  };

  const getContractGroup = async () => {
    const contractGroup = await getContractGroupDetail(queryParams.id);
    setContractGroupDetail(contractGroup);
    const inputList = contractGroup?.inputList;

    const newList = customFieldList.map((item) => {
      const registeredField = inputList?.find((field) => field.fieldId === item.fieldId);
      return registeredField ? registeredField : { ...item, inputValue: "", optionList: [] };
    });

    setValue("groupId", contractGroup?.groupId!);
    setValue("groupName", contractGroup?.groupName!);
    setValue("groupStatus", contractGroup?.groupStatus!);
    newList?.forEach((item, idx) => {
      setValue(`inputList.${idx}.fieldId`, item.fieldId);
      setValue(`inputList.${idx}.inputValue`, item.inputValue);
      setValue(`inputList.${idx}.optionList`, item.optionList);
    });
  };
  // initialData가 로드되면 form reset
  useEffect(() => {
    if (queryParams.id) {
      // 수정 모드: 기존 데이터 로드
      getContractGroup();
      fetchContractMapOfGroup();
    } else {
      // 신규 모드: initialData로 폼 초기화
      if (initialData) {
        reset(initialData);
      }
    }
  }, [initialData, queryParams.id]);

  // 변경된 데이터만 추출하는 함수
  const getChangedData = (formData: ContractGroupModel): ContractGroupModel => {
    // 신규 등록이거나 원본 데이터가 없으면 전체 데이터 반환
    if (!contractGroupDetail || formData.groupId === 0) {
      return formData;
    }

    // 변경된 데이터만 담을 객체 (groupId는 필수)
    const editRequest: ContractGroupModel = {
      groupId: formData.groupId,
    } as ContractGroupModel;

    // 1. groupName 비교
    if (!_.isEqual(contractGroupDetail.groupName, formData.groupName)) {
      editRequest.groupName = formData.groupName;
    }

    // 2. inputList 비교 - fieldId가 같은 것들끼리 비교
    const changedInputList: any[] = [];

    formData.inputList?.forEach((formInput) => {
      const originalInput = contractGroupDetail.inputList?.find((orig) => orig.fieldId === formInput.fieldId);

      if (originalInput) {
        // 같은 fieldId를 가진 항목이 있으면 inputValue와 optionList 비교
        const inputValueChanged = !_.isEqual(originalInput.inputValue, formInput.inputValue);
        const optionListChanged = !_.isEqual(originalInput.optionList, formInput.optionList);

        if (inputValueChanged || optionListChanged) {
          changedInputList.push({
            fieldId: formInput.fieldId,
            inputValue: formInput.inputValue,
            optionList: formInput.optionList,
          });
        }
      } else {
        // 원본에 없는 새로운 fieldId면 추가
        changedInputList.push(formInput);
      }
    });

    // 변경된 inputList가 있으면 추가
    if (changedInputList.length > 0) {
      editRequest.inputList = changedInputList;
    }

    return editRequest;
  };

  const onSubmit = async (passData: ContractGroupModel) => {
    // 변경된 데이터만 추출
    const request = getChangedData(passData);

    const execute = async () => {
      const { data } = await addContractGroup(request);
      navigate(PagePath.contractGroup.detail.replace(":id", String(data.data.groupId)));
    };

    setBaseModal({
      title: "저장하시겠습니까?",
      isOpen: true,
      onClick: () => {
        execute();
        setBaseModal({ isOpen: false });
      },
      onClose: () => {
        setBaseModal({ isOpen: false });
      },
      btnLeftTitle: "취소",
      btnRightTitle: "저장",
    });
  };
  const onError = (errors: FieldErrors<ContractGroupModel>) => {
    console.log(errors);
  };

  return (
    <div>
      <ContentsTitle title="" tabs={CONTRACT_GROUP_TABS} activeTab={activeTab} clickTab={handleTabClick} />
      {modalOpen && (
        <BaseModal isOpen={modalOpen} btnRightTitle="확인" onClick={() => setModalOpen(false)} title="기본정보가 저장되지 않았습니다"></BaseModal>
      )}
      <form onSubmit={handleSubmit(onSubmit, onError)}>
        <div className="contents-container__scroll">
          <div className="contents-container__wrap ">
            <ContractGroupInfoForm control={control} contractSubjectInfo={contractSubjectInfo} groupStatus={watch("groupStatus") || ""} />
            <ContractGroupCustomFieldForm customFieldList={customFieldList} control={control} />
          </div>
        </div>
        <div className="contents-container__btn-wrap">
          <div className="left-area"></div>
          <div className="right-area">
            {isAuthority("w") && (
              <>
                <EditCancelButton
                  to={queryParams.id ? PagePath.contractGroup.detail.replace(":id", String(queryParams.id)) : PagePath.contractGroup.list}
                />
                <BaseButton type="submit" title={"저장"} className="size-large" />
              </>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default ContractGroupForm;
